import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useBlocker, useNavigate } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import type { LabItemInput } from '@site/shared/content';
import { RESERVED_IDS, ValidationError, useLabItems, useSaveLabItem, type LabRow } from '@/lib/work';
import { Page, PageHeader } from '@/components/Shell';
import { ChipInput, Field, FormSection, TextList } from '@/components/form';
import { LoadError } from '@/components/ui';

/**
 * A lab prototype. Deliberately a different shape from a case study, because
 * the site must never let one read as client work. The status line is shown
 * verbatim as a chip on the site, and it is the honest one.
 */

const EMPTY: LabItemInput = { id: '', name: '', blurb: '', status: '', notes: [''], stack: [] };

const slugify = (s: string) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

export function LabEditorScreen({ id }: { id: string }) {
  const lab = useLabItems();
  const back = (
    <Link to="/work" className="btn btn-ghost btn-icon -ml-2" aria-label="Back to Work">
      <ArrowLeft className="size-5" aria-hidden />
    </Link>
  );
  if (lab.isPending)
    return (
      <>
        <PageHeader title="Lab item" back={back} />
        <Page>
          <div className="skeleton h-80 w-full max-w-3xl" />
        </Page>
      </>
    );
  if (lab.error)
    return (
      <>
        <PageHeader title="Lab item" back={back} />
        <Page>
          <LoadError error={lab.error} retry={() => lab.refetch()} />
        </Page>
      </>
    );
  const row = id === 'new' ? null : lab.data.find((l) => l.id === id) ?? null;
  if (id !== 'new' && !row)
    return (
      <>
        <PageHeader title="Lab item" back={back} />
        <Page>
          <p className="text-fg-dim">There is no lab item called "{id}".</p>
        </Page>
      </>
    );
  return <Editor key={row?.id ?? 'new'} row={row} back={back} taken={new Set(lab.data.map((l) => l.id))} />;
}

function Editor({ row, back, taken }: { row: LabRow | null; back: React.ReactNode; taken: Set<string> }) {
  const navigate = useNavigate();
  const save = useSaveLabItem();
  const isNew = !row;
  const initial = useMemo<LabItemInput>(
    () => (row ? { ...structuredClone(EMPTY), ...(row.data as object), id: row.id } : structuredClone(EMPTY)),
    [row],
  );
  const [item, setItem] = useState<LabItemInput>(initial);
  const [errors, setErrors] = useState<Map<string, string>>(new Map());
  const [banner, setBanner] = useState<{ ok: boolean; text: string } | null>(null);
  const dirty = JSON.stringify(item) !== JSON.stringify(initial);
  const leaving = useRef(false);

  useBlocker({ shouldBlockFn: () => !leaving.current && dirty && !window.confirm('Leave without saving your changes?') });
  useEffect(() => {
    const h = (e: BeforeUnloadEvent) => dirty && e.preventDefault();
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [dirty]);

  const set = <K extends keyof LabItemInput>(k: K, v: LabItemInput[K]) => setItem((p) => ({ ...p, [k]: v }));
  const err = (k: string) => errors.get(k);

  async function onSave() {
    if (isNew && (taken.has(item.id) || RESERVED_IDS.has(item.id))) {
      setErrors(new Map([['id', 'That id is taken. Pick another.']]));
      return;
    }
    const clean: LabItemInput = { ...item };
    if (clean.evidence && !clean.evidence.href) delete clean.evidence;
    if (clean.schematic && !clean.schematic.src) delete clean.schematic;
    try {
      const id = await save.mutateAsync({ item: clean, isNew });
      setErrors(new Map());
      setBanner({ ok: true, text: 'Saved. Publish from Work to put it on the site.' });
      if (isNew) {
        leaving.current = true;
        navigate({ to: '/work/lab/$labId', params: { labId: id }, replace: true });
      }
    } catch (e) {
      if (e instanceof ValidationError) {
        setErrors(e.byPath);
        setBanner({ ok: false, text: 'Some fields need attention before this can be saved.' });
      } else setBanner({ ok: false, text: e instanceof Error ? e.message : String(e) });
    }
  }

  return (
    <>
      <PageHeader title={item.name || (isNew ? 'New lab item' : row!.id)} back={back} />
      <Page>
        <form className="grid max-w-3xl gap-10 pb-28" onSubmit={(e) => (e.preventDefault(), onSave())} noValidate>
          <FormSection title="The prototype">
            <Field label="Name" htmlFor="l-name" error={err('name')}>
              <input
                id="l-name"
                className="input"
                value={item.name}
                onChange={(e) => {
                  const name = e.target.value;
                  setItem((p) => ({ ...p, name, ...(isNew ? { id: slugify(name) } : {}) }));
                }}
              />
            </Field>
            <Field label="Id" htmlFor="l-id" error={err('id')} hint={isNew ? 'Fixed once saved.' : 'Fixed: services refer to it.'}>
              <input id="l-id" className="input num" value={item.id} disabled={!isNew} onChange={(e) => set('id', slugify(e.target.value))} />
            </Field>
            <Field label="What the machine does, in one line" htmlFor="l-blurb" error={err('blurb')}>
              <textarea id="l-blurb" className="input" rows={2} value={item.blurb} onChange={(e) => set('blurb', e.target.value)} />
            </Field>
            <Field
              label="Honest status"
              htmlFor="l-status"
              error={err('status')}
              hint="Shown word for word as a chip. Say what is true today: “Prototype · runs end to end · known rough edges”."
            >
              <input id="l-status" className="input" value={item.status} onChange={(e) => set('status', e.target.value)} />
            </Field>
          </FormSection>

          <FormSection title="The engineering" lede="Short lines, no adjectives: what is worth talking about.">
            <TextList value={item.notes} onChange={(v) => set('notes', v)} noun="note" rows={2} errors={errors} errorKey="notes" />
            <Field label="Stack" htmlFor="l-stack" error={err('stack')} hint="Press Enter after each one.">
              <ChipInput id="l-stack" value={item.stack} onChange={(v) => set('stack', v)} />
            </Field>
          </FormSection>

          <FormSection title="Evidence" lede="Optional: a live artefact the thing produced, never a marketing link.">
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Label" htmlFor="l-ev-label" error={err('evidence.label')}>
                <input
                  id="l-ev-label"
                  className="input"
                  value={item.evidence?.label ?? ''}
                  onChange={(e) => set('evidence', { label: e.target.value, href: item.evidence?.href ?? '', note: item.evidence?.note ?? '' })}
                />
              </Field>
              <Field label="Link" htmlFor="l-ev-href" error={err('evidence.href')}>
                <input
                  id="l-ev-href"
                  className="input"
                  inputMode="url"
                  value={item.evidence?.href ?? ''}
                  onChange={(e) => set('evidence', { label: item.evidence?.label ?? '', href: e.target.value, note: item.evidence?.note ?? '' })}
                />
              </Field>
            </div>
            <Field label="What to expect there" htmlFor="l-ev-note" error={err('evidence.note')}>
              <input
                id="l-ev-note"
                className="input"
                value={item.evidence?.note ?? ''}
                onChange={(e) => set('evidence', { label: item.evidence?.label ?? '', href: item.evidence?.href ?? '', note: e.target.value })}
              />
            </Field>
          </FormSection>

          {item.schematic && (
            <FormSection title="Diagram" lede="The image file lives in the site's public/ folder; change it in code.">
              <Field label="Caption" htmlFor="l-sc-cap" error={err('schematic.caption')}>
                <textarea
                  id="l-sc-cap"
                  className="input"
                  rows={3}
                  value={item.schematic.caption}
                  onChange={(e) => set('schematic', { ...item.schematic!, caption: e.target.value })}
                />
              </Field>
              <p className="num text-xs text-fg-dim">
                {item.schematic.src} · {item.schematic.width}×{item.schematic.height}
              </p>
            </FormSection>
          )}
        </form>
      </Page>

      <div className="fixed inset-x-0 bottom-[calc(4.25rem+env(safe-area-inset-bottom))] z-20 border-t border-edge bg-paper lg:bottom-0 lg:left-60">
        <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-3 sm:px-6 lg:px-8">
          <p className={`min-w-0 flex-1 truncate text-sm ${banner ? (banner.ok ? 'text-ok' : 'text-alert') : 'text-fg-dim'}`}>
            {banner?.text ?? (dirty ? 'Unsaved changes.' : 'Saved.')}
          </p>
          <button type="button" className="btn btn-primary" disabled={save.isPending || !dirty} onClick={onSave}>
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </>
  );
}
